<template>
    
    <div class="section text-center mt-2 mb-2" v-if="loading_list">
        <div class="spinner-border text-secondary" role="status"></div>
    </div>
    <div class="section mt-2 mb-2" v-if="!loading_list">
    <div class="section-title mal-text">{{$t('summary_title')}}</div>
        <div class="card">
            <div class="card-body">
                <Form @submit="storeBasicInfo" :validation-schema="schema">
                    <div class="form-group boxed">
                        <div class="input-wrapper">
                            <label class="label mal-text" for="text4b">{{$t('agreement_title')}}</label>
                            <div class="agreement-text mal-text">
                                <p>{{$t('agreement_paragraph')}}</p>
                            </div>
                            <div class="form-check mt-3">
                                <input type="checkbox" value="Y" class="form-check-input" id="i_agree_checkbox"
                                    v-model="i_agree" :disabled="disable_all || profile.biodata_updated === 'Y'"
                                    name="i_agree">
                                <label class="form-check-label mal-text" for="i_agree_checkbox">{{$t('agree_label')}}</label>
                            </div>
                        </div>
                        <ErrorMessage name="i_agree" class="error-feedback" />
                    </div>

                    <!-- Signature Section -->
                    <div class="form-group boxed mt-4" v-if="i_agree">
                        <div class="input-wrapper">
                            <!-- Show info if viewing someone else's biodata -->
                            <div class="alert alert-info" v-if="disable_all">
                                <small class="mal-text">{{$t('viewing_other_notice')}}</small>
                            </div>

                            <!-- Display existing signature -->
                            <div v-if="hasExistingSignature && !showSignaturePad && (this.profile.current_location_type == 'outside_state' || this.profile.current_location_type == 'abroad')"
                                class="existing-signature-container">
                                <label class="label mal-text">{{$t('member_signature')}} <span class="mal-text">({{$t('member_signature_mal')}})</span></label>
                                <div class="existing-signature-display">
                                    <img :src="getSignatureUrl()" alt="Member Signature" class="signature-image">
                                    <div class="signature-actions mt-3" v-if="!disable_all">
                                        <button type="button" class="btn btn-outline-primary btn-sm" @click="showSignaturePad = true">
                                            <ion-icon name="create-outline"></ion-icon>
                                            {{$t('reupload_signature')}}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Show signature pad for new signatures or re-upload -->
                            <div v-if="(!hasExistingSignature || showSignaturePad) && (this.profile.current_location_type == 'outside_state' || this.profile.current_location_type == 'abroad')">
                                <SignaturePad ref="signaturePad" :label="$t('member_signature')" :malayalam-label="$t('member_signature_mal')" :required="true" :canvas-width="320" :canvas-height="120" :disabled="disable_all" v-model="signature" @signature-saved="onSignatureSaved" @signature-cleared="onSignatureCleared" />

                                <!-- Cancel re-upload option -->
                                <div v-if="hasExistingSignature && showSignaturePad" class="mt-2">
                                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="cancelReUpload">
                                        <ion-icon name="arrow-back-outline"></ion-icon>
                                        {{$t('cancel_keep_signature')}}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="transparent pt-3" v-if="currentUser.id == profile.user_id">
                        <button class="btn btn-primary btn-block btn-lg" :disabled="loading">
                            <span v-show="loading" class="spinner-border spinner-border-sm"></span>
                            <span v-if="profile.biodata_updated === 'Y'">{{$t('save')}}</span>
                            <span v-else>{{$t('save_next')}}</span>
                        </button>
                    </div>
                    <div class="transparent pt-3" v-if="currentUser.id != profile.user_id">
                        <button type="button" @click="goToNextPage" class="btn btn-primary btn-block btn-lg"
                            :disabled="loading">
                            <span v-show="loading" class="spinner-border spinner-border-sm"></span>
                            <span>{{$t('next')}}</span>
                        </button>
                    </div>
                </Form>
            </div>
        </div>
    </div>
</template>
<script>
import { useI18n } from 'vue-i18n';
import { Form, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import UserService from "../../services/user.service";
import SignaturePad from "../../components/common/SignaturePad.vue";
export default {
    setup() {
        const { locale } = useI18n();
        return { locale };
    },
    name: 'Basic Info',
    components: {
        Form,
        ErrorMessage,
        SignaturePad,
    },
    data() {
        const schema = yup.object().shape({
            i_agree: yup.boolean().oneOf([true], "Please agree"),
        });

        return {
            loading: false,
            loading_list: false,
            message: "",
            schema,
            i_agree: false,
            profile: [],
            disable_all: false,
            signature: null,
            signature_date: '',
            signature_place: '',
            showSignaturePad: false
        };
    },
    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        },
        hasExistingSignature() {
            return this.profile.signature && this.profile.signature !== null && this.profile.signature !== '';
        }
    },
    mounted() {
        if (!this.currentUser) {
            location.replace('/');
        }
        this.loading_list = true;
        this.signature_date = this.getCurrentDate();
        this.getProfile();
        this.$root.updateParent(this.$t("bio_data"));
    },
    methods: {
        switchLanguage(lang) {
            this.$i18n.locale = lang;
        },
        getCurrentDate() {
            const today = new Date();
            return today.toISOString().split('T')[0];
        },
        onSignatureSaved(signatureData) {
            this.signature = signatureData;
            console.log('Signature saved:', signatureData);

            // Auto-save the signature when it's drawn
            this.saveSignatureToServer(signatureData);
        },

        async saveSignatureToServer(signatureData) {
            if (!signatureData) {
                console.error('No signature data to save');
                return;
            }

            try {
                // Show loading state for signature save
                const signaturePad = this.$refs.signaturePad;
                if (signaturePad) {
                    signaturePad.loading = true;
                }

                // Prepare the signature data for upload
                const formData = new FormData();

                // Convert base64 to blob
                const blob = this.dataURLtoBlob(signatureData);
                formData.append('signature', blob, `signature_${this.profile.id}_${Date.now()}.png`);
                formData.append('id', this.profile.id);
                formData.append('user_id', this.currentUser.id);
                formData.append('submit_type', 'signature');

                const response = await UserService.authPostRequest('store-profile', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                if (response.data.status === 'success') {
                    // Update profile with the actual signature URL from response
                    if (response.data.data && response.data.data.signature) {
                        this.profile.signature = response.data.data.signature;
                    }

                    // Hide signature pad and show existing signature
                    this.showSignaturePad = false;

                    this.$swal.fire({
                        icon: 'success',
                        title: 'Signature Saved!',
                        text: 'നിങ്ങളുടെ ഒപ്പ് വിജയകരമായി സേവ് ചെയ്തു',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000
                    });
                } else {
                    throw new Error(response.data.message || 'Failed to save signature');
                }
            } catch (error) {
                console.error('Signature save error:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'ഒപ്പ് സേവ് ചെയ്യുന്നതിൽ പരാജയപ്പെട്ടു. ദയവായി വീണ്ടും ശ്രമിക്കുക.',
                    confirmButtonText: 'ശരി'
                });
            } finally {
                // Remove loading state
                const signaturePad = this.$refs.signaturePad;
                if (signaturePad) {
                    signaturePad.loading = false;
                }
            }
        },

        // Helper method to convert dataURL to Blob
        dataURLtoBlob(dataURL) {
            const arr = dataURL.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }

            return new Blob([u8arr], { type: mime });
        },
        onSignatureCleared() {
            this.signature = null;
            console.log('Signature cleared');
        },
        validateSignature() {
            console.log('Validation check:', {
                i_agree: this.i_agree,
                signature: !!this.signature,
                signature_date: this.signature_date,
                signature_place: this.signature_place,
                disable_all: this.disable_all
            });

            // Only validate if user agreed and not viewing someone else's biodata

            return true;
        },
        getProfile() {
            var data = {
                id: this.$route.params.id
            }
            UserService.authPostRequest('get-profile', data).then(
                (response) => {
                    this.profile = response.data.data.list;

                    console.log('Profile loaded:', {
                        profile_user_id: this.profile.user_id,
                        current_user_id: this.currentUser.id,
                        biodata_updated: this.profile.biodata_updated,
                        disable_all_will_be: this.profile.user_id != this.currentUser.id
                    });

                    if (this.profile.user_id != this.currentUser.id) {
                        this.disable_all = true;
                        this.$root.updateParent(this.profile.name + " യുടെ ബയോ ഡാറ്റ");
                    } else {
                        this.disable_all = false; // Ensure it's editable for the owner
                    }

                    this.i_agree = this.profile.biodata_updated === 'Y';

                    // Load existing signature data if available
                    if (this.profile.signature) {
                        this.signature = this.profile.signature;
                    }
                    if (this.profile.signature_date) {
                        this.signature_date = this.profile.signature_date;
                    }
                    if (this.profile.signature_place) {
                        this.signature_place = this.profile.signature_place;
                    }

                    console.log('Final state:', {
                        disable_all: this.disable_all,
                        i_agree: this.i_agree,
                        has_signature: !!this.signature
                    });

                    this.loading_list = false;
                },
            );
        },
        storeBasicInfo(data) {
            // Validate signature before submitting
            if (!this.validateSignature()) {
                return;
            }

            data.id = this.$route.params.id;
            data.user_id = this.currentUser.id;
            data.i_agree = this.i_agree;
            data.submit_type = 'agreement';
            data.signature = this.signature;
            data.signature_date = this.signature_date;
            data.signature_place = this.signature_place;
            this.loading = true;
            console.log(data);
            UserService.authPostRequest('store-profile', data).then(
                (response) => {
                    if (response.data.status == 'success') {
                        this.$swal('', 'നിങ്ങളുടെ ബയോ ടാറ്റ ചേർക്കൽ പൂർത്തിയായി.', 'seccess');
                        this.loading = false;
                        // if (this.profile.biodata_updated !== 'Y') {
                        this.$router.push('/membership-dashboard');
                        // }
                    } else if (response.data.status == 'error') {
                        this.$swal.fire({
                            icon: 'warning',
                            title: 'Warning!',
                            text: response.data.message,
                            toast: true,
                            position: 'center',
                            showConfirmButton: false,
                            timer: 2000
                        });
                        this.message = response.data.message;
                        this.loading = false;
                    } else {
                        this.loading = false;
                    }
                },
                (error) => {
                    this.loading = false;

                    this.messafe = (error.response &&
                        error.response.data &&
                        error.response.data.message) ||
                        error.message ||
                        error.toString();
                }
            );
        },

        // Get signature URL for display
        getSignatureUrl() {
            if (!this.profile.signature) return '';

            // If it's already a full URL (from API response), return as is
            if (this.profile.signature.startsWith('http')) {
                return this.profile.signature;
            }

            // If it's a relative path, construct the full URL
            return `https://guide.msoftit.com/${this.profile.signature}`;
        },

        // Cancel re-upload and keep existing signature
        cancelReUpload() {
            this.showSignaturePad = false;
            this.signature = null;

            // Clear the signature pad if it exists
            if (this.$refs.signaturePad) {
                this.$refs.signaturePad.clearSignature();
            }
        },
        goToNextPage() {
            this.$router.push('/bio-data/' + this.profile.user_id);
        }
    },
};
</script>
<style scoped>
.error-feedback {
    color: red;
}

.label {
    font-size: 12px !important;
}

.form-control {
    height: 30px !important;
}

.mobile-field {
    margin-top: 23px;
}

.hr-line {
    height: 8px !important;
    background-color: #1a418c;
    opacity: 75%;
}

.form-check-input:disabled~.form-check-label,
.form-check-input[disabled]~.form-check-label {
    opacity: 1 !important;
}

.listview-title {
    color: #1a418c;
    padding: 1px 0px;
    font-size: 17px;
    font-weight: 900;
}

.section-title {
    color: #1a418c;
    padding: 1px 0%;
    font-size: 17px;
    font-weight: 900;
}

.agreement-text {
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 5px;
    margin-bottom: 15px;
    border: 1px solid #dee2e6;
    font-size: 14px;
    line-height: 1.5;
    max-height: 200px;
    overflow-y: auto;
}

.agreement-text p {
    margin-bottom: 10px;
}

.agreement-text p:last-child {
    margin-bottom: 0;
}

/* Signature specific styles */
.signature-section {
    border-top: 1px solid #dee2e6;
    padding-top: 20px;
    margin-top: 20px;
}

.signature-info {
    background-color: #e7f3ff;
    border: 1px solid #b3d9ff;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 15px;
}

.signature-info p {
    margin: 0;
    font-size: 13px;
    color: #1a418c;
}

.col-6 .form-control {
    height: 35px !important;
}

@media (max-width: 768px) {
    .signature-pad-container {
        margin: 0 -10px;
    }
}

/* Existing signature display styles */
.existing-signature-container {
    margin-bottom: 20px;
}

.existing-signature-display {
    border: 2px solid #e1e5e9;
    border-radius: 8px;
    padding: 20px;
    background: #ffffff;
    text-align: center;
}

.signature-image {
    max-width: 100%;
    max-height: 150px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background-color: white;
    padding: 10px;
    display: block;
    margin: 0 auto;
}

.signature-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    flex-wrap: wrap;
}

.signature-actions .btn {
    min-width: 140px;
}

/* Responsive adjustments */
@media (max-width: 576px) {
    .signature-actions {
        flex-direction: column;
        align-items: center;
    }

    .signature-actions .btn {
        width: 100%;
        max-width: 250px;
    }

    .existing-signature-display {
        padding: 15px;
    }
}
</style>